import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable, OnDestroy, Type } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EuiMessageBoxContainerComponent } from '../container/eui-message-box-container.component';
import { EuiMessageBoxInterface } from '../models/eui-message-box.config';

@Injectable()
export class EuiMessageBoxService implements OnDestroy {

    private overlayRef: OverlayRef;
    private destroy$: Subject<boolean> = new Subject<boolean>();
    private euiMessageBoxContainerComponent: Type<EuiMessageBoxContainerComponent> = EuiMessageBoxContainerComponent;

    constructor(private overlay: Overlay) {}

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
        this.overlayRef = null;
    }

    /**
     * Open a Message Box
     *
     * @param {EuiMessageBoxInterface} config Configuration applied to the message box.
     */
    public openMessageBox(config: EuiMessageBoxInterface): void {
        const positionStrategy = this.overlay.position()
            .global()
            .centerHorizontally()
            .centerVertically();

        this.overlayRef = this.overlay.create({
            backdropClass: 'eui-message-box__backdrop',
            hasBackdrop: true,
            width: '50rem',
            positionStrategy,
        });

        const containerPortal = new ComponentPortal(this.euiMessageBoxContainerComponent);
        const containerRef: ComponentRef<EuiMessageBoxContainerComponent> = this.overlayRef.attach(containerPortal);

        this.overlayRef.outsidePointerEvents().pipe(takeUntil(this.destroy$)).subscribe((e) => {
            if (config.closeOnClickOutside) {
                config.outsideClick?.();
                containerRef.instance.closeMessageBox();
            }
        });

        containerRef.instance.title = config.title;
        containerRef.instance.content = config.content;
        containerRef.instance.footer = config.footer;
        containerRef.instance.type = config.type;
        containerRef.instance.hasCloseButton = config.hasCloseButton;
        containerRef.instance.closeOnClickOutside = config.closeOnClickOutside;
        containerRef.instance.acceptLabel = config.acceptLabel;
        containerRef.instance.dismissLabel = config.dismissLabel;
        containerRef.instance.outsideClick.pipe(takeUntil(this.destroy$)).subscribe(() => {
            config.outsideClick?.();
        });
        containerRef.instance.open.pipe(takeUntil(this.destroy$)).subscribe(() => {
            config.open?.();
        });
        containerRef.instance.close.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.closeMessageBox();
            config.close?.();
        });
        containerRef.instance.dismiss.pipe(takeUntil(this.destroy$)).subscribe(() => {
            config.dismiss?.();
        });
        containerRef.instance.accept.pipe(takeUntil(this.destroy$)).subscribe(() => {
            config.accept?.();
        });
    }

    /**
     * Close a Message Box
     */
    public closeMessageBox(): void {
        this.overlayRef.dispose();
        this.overlayRef = null;
    }
}
